import { useState, useEffect } from 'react';
import { connect } from 'redux-bundler-react';
import { useRequest, useModal, useApp } from '@stores';

import { saveRequest } from '@forms/input-forms/helpers';
import Icon from '@components/icon/Icon';
import { mdiArrowLeftThick, mdiCloudUploadOutline, mdiMapMarkerRadius, mdiFilePdfBox } from '@mdi/js';
import { Button } from '@trussworks/react-uswds';

import ProjectLocationModal from '../modals/ProjectLocationModal';
import NavbarNavigateWarningModal from '../modals/NavbarNavigateWarningModal';

import TimeAgo from 'react-timeago';

import './FormHeader.scss';
import LoaderButton from '@components/loader/LoaderButton';
import LinkButton from '@components/link/linkButton';
import RequestAccessModal from '@pages/common/modals/RequestAccess';
import RequestPermissionDisplay from '@src/app-components/permission-display/RequestPermissionDisplay';
import { AuthorizedUserPermissions } from '@api/services/authorized-user/types';
import { useNonPcnUpdateMutation } from '@src/api/services/non-pcn/hooks/useNonPcnUpdateMutation';
import { useFileStorageUploadMutation } from '@src/api/services/file-storage/hooks/useFileStorageUploadMutation';
import { useNavigate } from '@tanstack/react-router';

const iconProps = {
  focusable: false,
  size: 1,
};

const formatter = (value, unit, suffix) => {
  if (unit !== 'second' && suffix !== 'from now') {
    return ['Last saved:', value, unit + (value !== 1 ? 's' : ''), suffix].join(' ');
  }

  if (suffix === 'ago') {
    return 'Last saved: a few seconds ago';
  }

  if (suffix === 'from now') {
    return 'Last saved: Just now';
  }
};

const FormHeader = connect(
  'doGenerateDraftDocuments',
  'selectProjectID',
  'selectRequestFormData',
  'selectRequestAPIData',
  'selectIsAnonymous',
  ({
    doGenerateDraftDocuments,
    requestFormData,
    requestAPIData,
    header,
    isAnonymous,
    disableSaveProgress,
    disableGeneratePdf,
  }) => {
    const [modifiedDate, setModifiedDate] = useState(new Date());
    const navigate = useNavigate();

    const { openModal } = useModal();
    const { isFormReadOnly: isReadOnly, selectedRequestId } = useRequest();
    const { isSavingProgress } = useApp();
    const { permits, requestType, requestID, version } = requestFormData?.request;

    const reqTypeId = Number(selectedRequestId);
    const locationText = selectedRequestId === '6' ? 'Site Location' : 'Project Location';
    const canSaveVC = selectedRequestId === '6' && !isAnonymous;
    const isPermitApplication = selectedRequestId === '12';
    const hasSelectedPermitType = permits?.length > 0 && !!permits?.[0]?.requestedPermitType;
    const isDownloadDisabled = isPermitApplication && !hasSelectedPermitType;
    const permission = requestAPIData?.request?.permission;

    const hasWriteAccess = [AuthorizedUserPermissions.Write, AuthorizedUserPermissions.Owner].includes(permission);
    const isUserOwner = permission === AuthorizedUserPermissions.Owner;

    const { mutateAsync: updateNonPcn } = useNonPcnUpdateMutation();
    const { mutateAsync: uploadRequestFiles } = useFileStorageUploadMutation();

    // @TODO: Add the rest of the request types as update logic gets migrated to React-Query/Zustand
    const updateFnMapping = {
      11: updateNonPcn,
    };

    const handleOnSave = () => {
      saveRequest(true, uploadRequestFiles, updateFnMapping[reqTypeId]);
    };

    const handleLocationClick = () => {
      const locationData = {
        ...requestFormData.location,
        siteDirections: requestFormData?.siteDirections,
        nearestWaterbody: requestFormData?.nearestWaterbody,
        parcelNumber: requestFormData?.parcelNumber,
      };

      openModal(ProjectLocationModal, {
        locationData: locationData,
        projectId: requestFormData?.projectID,
        version: requestFormData?.projectVersion,
      });
    };

    const handleExitClick = () => {
      if (isReadOnly) {
        navigate({ to: '/dashboard' });
        return;
      }
      openModal(NavbarNavigateWarningModal, { url: '/dashboard' });
    };

    useEffect(() => {
      const dateString = requestFormData?.request?.modifiedDate;

      if (!dateString) {
        return;
      }

      const lastModifiedZulu = new Date(dateString); //temp fix as time is being returned from DB as Zulu time when it is actually local server time

      setModifiedDate(lastModifiedZulu);
    }, [requestFormData?.request?.modifiedDate]);

    return (
      <div className='form-header-container'>
        <div className='text-container'>
          <div className='title-container'>
            <h1 className='header-text'>{header} - </h1>
            <div className='project-name'>{requestFormData?.projectName}</div>
            <div className='title-extras'>
              {permission && <RequestPermissionDisplay permission={permission} />}
              {isUserOwner && (
                <LinkButton
                  onClick={() =>
                    openModal(RequestAccessModal, {
                      requestId: requestID,
                      version: version,
                      projectName: requestFormData.projectName,
                      requestType: requestType,
                    })
                  }
                  title='Manage Access'
                  content='Manage Access'
                />
              )}
              {modifiedDate && <TimeAgo className='modified-text' date={modifiedDate} formatter={formatter} />}
            </div>
          </div>
        </div>
        <div className='buttons-container'>
          <div className='exit-button-container'>
            <Button className='button-small exit-button' onClick={handleExitClick} secondary title='Exit' type='button'>
              <Icon path={mdiArrowLeftThick} {...iconProps} />
              Exit Form
            </Button>
          </div>
          <div className='action-buttons-container'>
            {((!isReadOnly && !disableSaveProgress && hasWriteAccess) || canSaveVC) && (
              <LoaderButton
                className='action-button button-small save-progress'
                onClick={handleOnSave}
                isLoading={isSavingProgress}
              >
                <Icon path={mdiCloudUploadOutline} {...iconProps} />
                Save Progress
              </LoaderButton>
            )}
            {!isReadOnly && !disableGeneratePdf && (
              <Button
                className='action-button button-small draft-pdf-button'
                onClick={doGenerateDraftDocuments}
                title={
                  isDownloadDisabled
                    ? 'Download all draft documents with currently entered information for this request. This is currently disabled because a permit type has not yet been selected.'
                    : 'Download all draft documents with currently entered information for this request'
                }
                type='button'
                disabled={isDownloadDisabled}
              >
                <Icon path={mdiFilePdfBox} {...iconProps} />
                Download Draft PDF
              </Button>
            )}
            {
              <Button className='action-button button-small' onClick={handleLocationClick} type='button'>
                <Icon path={mdiMapMarkerRadius} {...iconProps} />
                {locationText}
              </Button>
            }
          </div>
        </div>
      </div>
    );
  }
);
export default FormHeader;
